// src/cockpit4/components/GeoDsoCommandCenter.tsx
// ============================================
// GEO+DSO 全局管控中台（GEO+DSO 商业化改造 第5层 + 第1层 Obsidian 双向互通底座）
//
// 背景：基于《XEAOS_详细改进执行计划_v2.0》第 9.1 节特别优化要求
//   - 第一层：Obsidian 知识库双向互通底座
//       · 正向：GEO 模块自动读取结构化标签数据，作为所有 AI 生成内容的固定约束基底
//       · 反向：GF 输出层抓取客户咨询/成交案例/用户提问后，自动归档写入 Obsidian
//   - 第五层：全局管控中台升级
//       · 一键体检：GEO 品牌信源健康度 / DSO 内容收录率 / GF 线索转化效率
//       · 编排任务分级：日常维稳 / 热点追更 / 新品推广 / 活动引流
//       · 舆情监控：全网平台 + 各大 AI 模型监测品牌负面信息
//
// 数据来源（禁止假数据）：
//   · Obsidian 文档 ← RuntimeManager.query('knowledge', { type: 'list_all_notes' / 'tags' })
//   · 双向同步写入 ← RuntimeManager.execute('knowledge', 'write_output' / 'sync')
//   · GEO 健康度  ← RuntimeManager.query('geo', { type: 'today_strategy' })
//   · DSO 健康度  ← RuntimeManager.query('dso', { type: 'distribution_plan' / 'matrix_status' })
//   · GF 健康度   ← RuntimeManager.query('growth_factory', { type: 'growth_overview' })
//   · 舆情数据    ← RuntimeManager.query('news', { type: 'get_latest' })
//   · GF 反向归档源 ← RuntimeManager.query('crm', { type: 'list' })
//   · 任务编排执行 ← RuntimeManager.execute('growth_factory', 'run_daily_growth' / 'run_stage')
//
// 主色调：紫色系（统管 GEO 蓝 / DSO 橙 / GF 绿），玻璃拟态深色科技风
// ============================================

import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Play,
  Plus,
  Brain,
  ArrowDownUp,
  FileText,
  BookOpen,
  Stethoscope,
  ListTodo,
  Megaphone,
  ShieldAlert,
  ChevronDown,
  Clock,
  AlertCircle,
  Target,
  Flame,
  Rocket,
  Sparkles,
  Layers,
  Factory,
  Wand2,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { RuntimeHealth } from "../../types/runtime";

// ============================================
// 类型定义（来自改进计划文档 9.1 第一层 + 第五层）
// ============================================

// Obsidian 知识库标签（6 类）
type ObsidianTag =
  | "brand_material"
  | "product_material"
  | "case_library"
  | "faq"
  | "testimonials"
  | "pricing";

// Obsidian 文档（双向互通视角）
interface ObsidianDocument {
  path: string;
  title: string;
  tags: ObsidianTag[];
  // 正向：被 GEO 读取
  geoReadCount: number;
  lastGeoReadAt?: string;
  // 反向：由 GF 写入
  writtenByGf: boolean;
  writtenAt?: string;
}

// Obsidian 双向同步状态
interface ObsidianSyncStatus {
  totalDocs: number;
  taggedDocs: number;
  tagDistribution: Record<ObsidianTag, number>;
  // 双向同步统计
  geoReads: number; // GEO 读取次数
  gfWritebacks: number; // GF 写入次数
  lastSyncAt: string;
}

// 一键体检结果
interface HealthCheckResult {
  // GEO 健康度
  geoHealth: {
    score: number; // 0-100
    brandSources: number; // 品牌信源数
    aiIndexedRate: number; // AI 收录率
    keywordCoverage: number; // 关键词覆盖率
    issues: string[];
  };
  // DSO 健康度
  dsoHealth: {
    score: number;
    publishedCount: number;
    indexedRate: number; // 收录率
    qualityRate: number; // 优质内容率
    issues: string[];
  };
  // GF 健康度
  gfHealth: {
    score: number;
    totalLeads: number;
    conversionRate: number;
    avgRoi: number;
    issues: string[];
  };
  // 整改清单
  actionItems: {
    priority: "high" | "medium" | "low";
    module: string;
    action: string;
    expectedImpact: string;
  }[];
  checkedAt: string;
}

// 任务编排分级
type TaskTier = "daily_maintenance" | "hot_topic" | "new_product" | "campaign";
type TaskStatus = "scheduled" | "running" | "completed" | "failed";

interface OrchestrationTask {
  id: string;
  tier: TaskTier;
  title: string;
  description: string;
  geoActions?: string[];
  dsoActions?: string[];
  gfActions?: string[];
  status: TaskStatus;
  scheduledAt: string;
  startedAt?: string;
  completedAt?: string;
  result?: string;
}

// 舆情监控告警
interface SentimentAlert {
  id: string;
  source: string; // 来源平台或 AI
  type: "negative_mention" | "brand_attack" | "fake_info" | "competitor_attack";
  content: string;
  severity: "low" | "medium" | "high" | "critical";
  detectedAt: string;
  handled: boolean;
  handlingNote?: string;
}

// ============================================
// 元数据：Obsidian 6 类标签 + 任务 4 类分级
// ============================================

// Obsidian 标签元数据（含中文标签 / 主题色 / 匹配关键词，用于真实文档自动分类）
const TAG_META: Record<
  ObsidianTag,
  { label: string; color: string; desc: string; matchKeywords: string[] }
> = {
  brand_material: {
    label: "品牌资料",
    color: "#A855F7",
    desc: "品牌圣经、品牌词、企业/个人品牌",
    matchKeywords: ["brand", "品牌", "01_brand", "02_brand", "品牌圣经", "brand_library"],
  },
  product_material: {
    label: "产品资料",
    color: "#3B82F6",
    desc: "SKU、产品手册、产品深度内容",
    matchKeywords: ["product", "产品", "sku", "04_product", "03_product", "产品手册"],
  },
  case_library: {
    label: "案例库",
    color: "#10B981",
    desc: "成交案例、客户案例、案例研究",
    matchKeywords: ["case", "案例", "05_case", "成交案例", "客户案例", "case_study"],
  },
  faq: {
    label: "FAQ",
    color: "#F59E0B",
    desc: "常见问题、用户提问、答疑",
    matchKeywords: ["faq", "问题", "06_faq", "常见问题", "qa", "答疑"],
  },
  testimonials: {
    label: "客户证言",
    color: "#EC4899",
    desc: "客户评价、客户证言、口碑",
    matchKeywords: ["testimonial", "证言", "07_testimonial", "客户评价", "口碑", "review"],
  },
  pricing: {
    label: "报价体系",
    color: "#06B6D4",
    desc: "报价单、价格表、定价策略",
    matchKeywords: ["pricing", "报价", "08_pricing", "价格", "定价", "quote"],
  },
};

// 任务分级元数据
const TASK_TIER_META: Record<
  TaskTier,
  { label: string; color: string; desc: string; icon: typeof Target }
> = {
  daily_maintenance: {
    label: "日常维稳",
    color: "#3B82F6",
    desc: "品牌词维稳、长尾词日常更新",
    icon: ShieldAlert,
  },
  hot_topic: {
    label: "热点追更",
    color: "#F59E0B",
    desc: "追行业热点、抢搜索卡位",
    icon: Flame,
  },
  new_product: {
    label: "新品推广",
    color: "#A855F7",
    desc: "新品发布、产品深度内容",
    icon: Rocket,
  },
  campaign: {
    label: "活动引流",
    color: "#10B981",
    desc: "营销活动、引流爆破",
    icon: Megaphone,
  },
};

// 舆情告警类型元数据
const ALERT_TYPE_META: Record<
  SentimentAlert["type"],
  { label: string; color: string }
> = {
  negative_mention: { label: "负面提及", color: "#F59E0B" },
  brand_attack: { label: "品牌攻击", color: "#EF4444" },
  fake_info: { label: "不实信息", color: "#8B5CF6" },
  competitor_attack: { label: "竞品攻击", color: "#06B6D4" },
};

// 告警严重度排序与配色
const SEVERITY_RANK: Record<SentimentAlert["severity"], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};
const SEVERITY_COLOR: Record<SentimentAlert["severity"], string> = {
  critical: "#EF4444",
  high: "#F97316",
  medium: "#F59E0B",
  low: "#6B7280",
};
const SEVERITY_LABEL: Record<SentimentAlert["severity"], string> = {
  critical: "紧急",
  high: "高",
  medium: "中",
  low: "低",
};

// 健康度配色
const HEALTH_COLOR: Record<RuntimeHealth, string> = {
  healthy: "#10B981",
  degraded: "#F59E0B",
  unhealthy: "#EF4444",
  unknown: "#6B7280",
};

// 三模块主题色（GEO 蓝 / DSO 橙 / GF 绿）+ 紫色统管
const MODULE_THEME = {
  geo: { color: "#3B82F6", label: "GEO", icon: Globe, cn: "生成式引擎优化" },
  dso: { color: "#F59E0B", label: "DSO", icon: Layers, cn: "数字搜索优化" },
  gf: { color: "#10B981", label: "GF", icon: Factory, cn: "增长工厂" },
  master: { color: "#A855F7", label: "中台", icon: Brain, cn: "全局管控" },
};

// 舆情负面关键词（用于扫描真实新闻标题/摘要，命中即生成告警）
const NEGATIVE_KEYWORDS = [
  "风险", "危机", "负面", "暴跌", "封禁", "下架", "漏洞", "数据泄露",
  "诉讼", "投诉", "维权", "退款", "欺诈", "虚假", "造假", "崩盘",
  "警告", "处罚", "违规", "黑产", "跑路", "翻车", "事故", "缺陷",
];

// v5.2 审计修复 P3-⑥：负面预警关键词池（品牌 × 负面词，可配置，localStorage）
const NEGATIVE_POOL_KEY = "xeaos_negative_pool";
/** 读取负面词池：用户配置 + 默认池合并去重 */
function loadNegativePool(): string[] {
  try {
    const raw = localStorage.getItem(NEGATIVE_POOL_KEY);
    const custom: string[] = raw ? JSON.parse(raw) : [];
    const merged = [...new Set([...NEGATIVE_KEYWORDS, ...(Array.isArray(custom) ? custom : [])])];
    return merged;
  } catch {
    return [...NEGATIVE_KEYWORDS];
  }
}
/** 保存用户负面词（仅存用户自定义部分） */
function saveNegativePoolCustom(words: string[]): void {
  try {
    localStorage.setItem(NEGATIVE_POOL_KEY, JSON.stringify(words));
  } catch { /* 忽略 */ }
}

// ============================================
// localStorage 持久化 Key（任务/告警/同步日志/体检历史）
// 这些是用户通过本中台触发的真实操作记录，非假数据
// ============================================
const LS_KEYS = {
  tasks: "xeaos_gdcc_tasks",
  alerts: "xeaos_gdcc_alerts",
  syncLog: "xeaos_gdcc_sync_log", // { geoReads, gfWritebacks, lastSyncAt, docReadMap }
  healthHistory: "xeaos_gdcc_health_history",
};

// ============================================
// 工具函数
// ============================================

// 读取 localStorage JSON
function loadLs<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

// 写入 localStorage JSON
function saveLs<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 容量超限或无 localStorage 时静默降级
  }
}

// 真实文档自动分类：基于 path / title / tags 命中关键词，归入 6 类标签
function classifyDocument(note: {
  path?: string;
  title?: string;
  fileName?: string;
  tags?: string[] | unknown;
}): ObsidianTag[] {
  const haystack = [
    (note.path || "").toLowerCase(),
    (note.title || "").toLowerCase(),
    (note.fileName || "").toLowerCase(),
  ].join(" ");
  // 兼容 tags 可能是字符串数组或对象数组
  let tagsStr = "";
  if (Array.isArray(note.tags)) {
    tagsStr = note.tags
      .map((t) => (typeof t === "string" ? t : (t as any)?.name || (t as any)?.label || ""))
      .join(" ")
      .toLowerCase();
  }
  const fullText = `${haystack} ${tagsStr}`;

  const matched: ObsidianTag[] = [];
  (Object.keys(TAG_META) as ObsidianTag[]).forEach((tag) => {
    if (TAG_META[tag].matchKeywords.some((kw) => fullText.includes(kw.toLowerCase()))) {
      matched.push(tag);
    }
  });
  return matched;
}

// 健康度评分 → 配色
function scoreColor(score: number): string {
  if (score >= 80) return "#10B981";
  if (score >= 60) return "#F59E0B";
  if (score >= 40) return "#F97316";
  return "#EF4444";
}

// 等待 RuntimeManager 初始化（统一工具函数，最长 30s）
// 使用 RuntimeManager.waitForRuntime（P2 性能优化：统一等待策略）

// 双层解包 Runtime 响应（统一工具函数：RuntimeManager.unwrapResponse 递归解包）

// ============================================
// 主组件
// ============================================

export default function GeoDsoCommandCenter() {
  // === 顶部 Tab ===
  const [activeTab, setActiveTab] = useState<
    "obsidian" | "health" | "tasks" | "sentiment"
  >("obsidian");

  // === 全局状态 ===
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  // === Obsidian 互通状态 ===
  const [obsidianDocs, setObsidianDocs] = useState<ObsidianDocument[]>([]);
  const [syncStatus, setSyncStatus] = useState<ObsidianSyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [writebacking, setWritebacking] = useState(false);

  // === 一键体检状态 ===
  const [healthResult, setHealthResult] = useState<HealthCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [healthHistory, setHealthHistory] = useState<
    Array<{ checkedAt: string; geo: number; dso: number; gf: number }>
  >([]);

  // === 任务编排状态 ===
  const [tasks, setTasks] = useState<OrchestrationTask[]>([]);
  const [creatingTask, setCreatingTask] = useState(false);
  const [newTask, setNewTask] = useState<{
    tier: TaskTier;
    title: string;
    description: string;
    scheduledAt: string;
  }>({
    tier: "daily_maintenance",
    title: "",
    description: "",
    scheduledAt: new Date().toISOString().slice(0, 16),
  });
  const [runningTaskId, setRunningTaskId] = useState<string | null>(null);

  // === 舆情监控状态 ===
  const [alerts, setAlerts] = useState<SentimentAlert[]>([]);
  const [scanning, setScanning] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [handlingNote, setHandlingNote] = useState<Record<string, string>>({});

  // ============================================
  // toast 自动消失
  // ============================================
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // ============================================
  // 加载 Obsidian 真实文档 + 同步日志
  // ============================================
  const loadObsidianData = useCallback(async () => {
    if (!RuntimeManager.hasRuntime(RuntimeName.Knowledge)) {
      setObsidianDocs([]);
      setSyncStatus(null);
      return;
    }
    try {
      const resp = await RuntimeManager.query(RuntimeName.Knowledge, {
        type: "list_all_notes",
      });
      const notes = RuntimeManager.unwrapResponse<any[]>(resp) || [];
      // 同步日志（记录本中台触发的真实 GEO 读取 / GF 写入次数）
      const syncLog = loadLs<{
        geoReads: number;
        gfWritebacks: number;
        lastSyncAt: string;
        docReadMap: Record<string, number>;
        docWriteMap: Record<string, string>;
      }>(LS_KEYS.syncLog, {
        geoReads: 0,
        gfWritebacks: 0,
        lastSyncAt: "",
        docReadMap: {},
        docWriteMap: {},
      });

      // 将真实笔记映射为 ObsidianDocument（含分类 + 读取/写入状态）
      const docs: ObsidianDocument[] = notes.map((n: any) => {
        const path = n.path || n.fileName || n.id || n.title || "";
        const title = n.title || n.fileName || path.split("/").pop() || "未命名";
        const tags = classifyDocument(n);
        return {
          path,
          title,
          tags,
          geoReadCount: syncLog.docReadMap[path] || 0,
          lastGeoReadAt:
            syncLog.docReadMap[path] > 0 ? syncLog.lastSyncAt : undefined,
          writtenByGf: !!syncLog.docWriteMap[path],
          writtenAt: syncLog.docWriteMap[path] || undefined,
        };
      });

      setObsidianDocs(docs);

      // 计算同步状态
      const tagDistribution: Record<ObsidianTag, number> = {
        brand_material: 0,
        product_material: 0,
        case_library: 0,
        faq: 0,
        testimonials: 0,
        pricing: 0,
      };
      let taggedDocs = 0;
      docs.forEach((d) => {
        if (d.tags.length > 0) {
          taggedDocs += 1;
          d.tags.forEach((t) => {
            tagDistribution[t] += 1;
          });
        }
      });

      setSyncStatus({
        totalDocs: docs.length,
        taggedDocs,
        tagDistribution,
        geoReads: syncLog.geoReads,
        gfWritebacks: syncLog.gfWritebacks,
        lastSyncAt: syncLog.lastSyncAt,
      });
    } catch (err: any) {
      console.warn("[GeoDsoCommandCenter] 加载 Obsidian 文档失败:", err);
      setObsidianDocs([]);
      setSyncStatus(null);
    }
  }, []);

  // ============================================
  // 初始加载：等待 RuntimeManager 就绪后加载所有 Tab 数据
  // ============================================
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ok = await RuntimeManager.waitForRuntime();
      if (!ok) {
        setError("RuntimeManager 初始化超时（30 秒），请刷新页面重试");
        return;
      }
      await loadObsidianData();
      // 任务 / 告警 / 体检历史 从 localStorage 恢复
      setTasks(loadLs<OrchestrationTask[]>(LS_KEYS.tasks, []));
      setAlerts(loadLs<SentimentAlert[]>(LS_KEYS.alerts, []));
      setHealthHistory(
        loadLs<Array<{ checkedAt: string; geo: number; dso: number; gf: number }>>(
          LS_KEYS.healthHistory,
          []
        )
      );
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [loadObsidianData]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ============================================
  // Obsidian 一键同步（真实调用 Knowledge.sync）
  // 同时触发 GEO 正向读取（执行 GEO 策略生成，会真实读取 Obsidian）
  // ============================================
  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setError(null);
    try {
      // 1. 调用 Knowledge.sync 真实同步 Vault
      if (RuntimeManager.hasRuntime(RuntimeName.Knowledge)) {
        await RuntimeManager.execute(RuntimeName.Knowledge, "sync", {});
      }
      // 2. 触发 GEO 正向读取（generate_strategy 会真实读取 Obsidian 品牌资料）
      let geoOk = false;
      if (RuntimeManager.hasRuntime(RuntimeName.GEO)) {
        const r = await RuntimeManager.execute(
          RuntimeName.GEO,
          "generate_strategy",
          {}
        );
        geoOk = !!r?.success;
      }
      // 3. 记录同步日志（真实操作计数）
      const syncLog = loadLs<{
        geoReads: number;
        gfWritebacks: number;
        lastSyncAt: string;
        docReadMap: Record<string, number>;
        docWriteMap: Record<string, string>;
      }>(LS_KEYS.syncLog, {
        geoReads: 0,
        gfWritebacks: 0,
        lastSyncAt: "",
        docReadMap: {},
        docWriteMap: {},
      });
      const nowIso = new Date().toISOString();
      const newReadMap = { ...syncLog.docReadMap };
      // 对所有已分类的"品牌资料/产品资料"文档累计 GEO 读取次数（这些是 GEO 真实会读取的基底）
      obsidianDocs.forEach((d) => {
        if (d.tags.includes("brand_material") || d.tags.includes("product_material")) {
          newReadMap[d.path] = (newReadMap[d.path] || 0) + 1;
        }
      });
      const newLog = {
        geoReads: syncLog.geoReads + (geoOk ? 1 : 0),
        gfWritebacks: syncLog.gfWritebacks,
        lastSyncAt: nowIso,
        docReadMap: newReadMap,
        docWriteMap: syncLog.docWriteMap,
      };
      saveLs(LS_KEYS.syncLog, newLog);
      setToast({
        message: geoOk
          ? "✅ Obsidian 同步完成，GEO 已读取品牌/产品资料"
          : "✅ Obsidian 同步完成（GEO 未注册，跳过正向读取）",
        type: "success",
      });
      await loadObsidianData();
    } catch (err: any) {
      const msg = err?.message || String(err);
      setError(msg);
      setToast({ message: `❌ 同步失败：${msg}`, type: "error" });
    } finally {
      setSyncing(false);
    }
  };

  // ============================================
  // GF 反向归档：从 CRM 抓取真实客户/咨询，写入 Obsidian（write_output）
  // ============================================
  const handleGfWriteback = async () => {
    if (writebacking) return;
    setWritebacking(true);
    setError(null);
    try {
      if (!RuntimeManager.hasRuntime(RuntimeName.Knowledge)) {
        setToast({ message: "❌ Knowledge Runtime 未注册", type: "error" });
        return;
      }
      // 1. 从 CRM 拉取真实客户列表（GF 输出层抓取客户咨询/成交案例）
      let customers: any[] = [];
      if (RuntimeManager.hasRuntime(RuntimeName.CRM)) {
        const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" });
        customers = RuntimeManager.unwrapResponse<any[]>(resp) || [];
      }
      // 2. 对每个客户生成一条归档记录，写入 Obsidian Outputs 目录
      const nowIso = new Date().toISOString();
      const writeCount = Math.min(customers.length, 5); // 单次最多归档 5 条，避免爆写
      let successCount = 0;
      for (let i = 0; i < writeCount; i++) {
        const c = customers[i];
        const title = `客户咨询归档-${c.name || c.id || i}-${nowIso.slice(0, 10)}`;
        const content = [
          `# 客户咨询归档`,
          ``,
          `- 客户名称：${c.name || c.id || "未知"}`,
          `- 来源：${c.source || c.channel || "CRM"}`,
          `- 状态：${c.status || "unknown"}`,
          `- 归档时间：${nowIso}`,
          `- 沟通摘要：${c.lastContact || c.notes || c.summary || "（暂无摘要）"}`,
          ``,
          `## 自动归类`,
          `- tags: [faq, case_library]`,
        ].join("\n");
        try {
          const r = await RuntimeManager.execute(
            RuntimeName.Knowledge,
            "write_output",
            {
              platform: "crm_archive",
              title,
              content,
              status: "archived",
            }
          );
          if (r?.success) successCount += 1;
        } catch {
          // 单条失败不阻塞整体
        }
      }
      // 3. 如果 CRM 无数据，仍允许写入一条"手动归档"占位（避免无客户时按钮无效）
      if (customers.length === 0) {
        const title = `GF反向归档-手动记录-${nowIso.slice(0, 10)}`;
        const content = [
          `# GF 反向归档记录`,
          ``,
          `- 归档时间：${nowIso}`,
          `- 数据源：CRM（无客户数据）`,
          `- 说明：本次为手动触发的归档占位，待 CRM 有真实客户咨询后再次执行将自动填充`,
          ``,
          `## 自动归类`,
          `- tags: [faq]`,
        ].join("\n");
        await RuntimeManager.execute(RuntimeName.Knowledge, "write_output", {
          platform: "gf_writeback",
          title,
          content,
          status: "archived",
        });
        successCount += 1;
      }
      // 4. 更新同步日志
      const syncLog = loadLs<{
        geoReads: number;
        gfWritebacks: number;
        lastSyncAt: string;
        docReadMap: Record<string, number>;
        docWriteMap: Record<string, string>;
      }>(LS_KEYS.syncLog, {
        geoReads: 0,
        gfWritebacks: 0,
        lastSyncAt: "",
        docReadMap: {},
        docWriteMap: {},
      });
      const newWriteMap = { ...syncLog.docWriteMap };
      // 标记最近归档的虚拟路径（实际路径由 Knowledge runtime 决定，这里用 title 占位）
      for (let i = 0; i < successCount; i++) {
        newWriteMap[`gf_writeback_${nowIso}_${i}`] = nowIso;
      }
      saveLs(LS_KEYS.syncLog, {
        ...syncLog,
        gfWritebacks: syncLog.gfWritebacks + successCount,
        lastSyncAt: nowIso,
        docWriteMap: newWriteMap,
      });
      setToast({
        message: `✅ GF 反向归档完成，共写入 ${successCount} 条至 Obsidian`,
        type: "success",
      });
      await loadObsidianData();
    } catch (err: any) {
      const msg = err?.message || String(err);
      setError(msg);
      setToast({ message: `❌ 反向归档失败：${msg}`, type: "error" });
    } finally {
      setWritebacking(false);
    }
  };

  // ============================================
  // 一键体检：并行查询 GEO / DSO / GF / Knowledge，基于真实数据计算评分
  // ============================================
  const handleHealthCheck = async () => {
    if (checking) return;
    setChecking(true);
    setError(null);
    try {
      // 并行查询三模块（任意失败降级为 0 分）
      const [geoResp, dsoPlanResp, dsoMatrixResp, gfResp, kResp] =
        await Promise.all([
          RuntimeManager.hasRuntime(RuntimeName.GEO)
            ? RuntimeManager.query(RuntimeName.GEO, { type: "today_strategy" })
            : Promise.resolve(null),
          RuntimeManager.hasRuntime(RuntimeName.DSO)
            ? RuntimeManager.query(RuntimeName.DSO, { type: "distribution_plan" })
            : Promise.resolve(null),
          RuntimeManager.hasRuntime(RuntimeName.DSO)
            ? RuntimeManager.query(RuntimeName.DSO, { type: "matrix_status" })
            : Promise.resolve(null),
          RuntimeManager.hasRuntime(RuntimeName.GrowthFactory)
            ? RuntimeManager.query(RuntimeName.GrowthFactory, {
                type: "growth_overview",
              })
            : Promise.resolve(null),
          RuntimeManager.hasRuntime(RuntimeName.Knowledge)
            ? RuntimeManager.query(RuntimeName.Knowledge, {
                type: "asset_stats",
              })
            : Promise.resolve(null),
        ]);

      const geo = RuntimeManager.unwrapResponse<any>(geoResp);
      const dsoPlan = RuntimeManager.unwrapResponse<any>(dsoPlanResp);
      const dsoMatrix = RuntimeManager.unwrapResponse<any>(dsoMatrixResp);
      const gf = RuntimeManager.unwrapResponse<any>(gfResp);
      const kStats = RuntimeManager.unwrapResponse<any>(kResp);

      // === GEO 健康度评分（品牌信源 + AI 收录率 + 关键词覆盖率） ===
      const brandKeywords = geo?.brandKeywords || [];
      const coreKeywords = geo?.coreKeywords || [];
      const contentDirections = geo?.contentDirections || [];
      const brandSources = brandKeywords.length;
      const keywordCoverage =
        coreKeywords.length > 0
          ? Math.min(100, coreKeywords.length * 15)
          : 0;
      // AI 收录率：基于 SERP 特征覆盖度（有 serpFeatures 的关键词占比）
      const kwWithSerp = coreKeywords.filter(
        (k: any) => Array.isArray(k.serpFeatures) && k.serpFeatures.length > 0
      ).length;
      const aiIndexedRate =
        coreKeywords.length > 0
          ? Math.round((kwWithSerp / coreKeywords.length) * 100)
          : 0;
      const geoScore = Math.min(
        100,
        Math.round(
          brandSources * 8 + keywordCoverage * 0.4 + aiIndexedRate * 0.3 + 15
        )
      );
      const geoIssues: string[] = [];
      if (brandSources === 0)
        geoIssues.push("品牌信源为空，Obsidian 中缺少品牌资料标签文档");
      if (coreKeywords.length === 0) geoIssues.push("今日 GEO 策略未生成关键词");
      if (aiIndexedRate < 30) geoIssues.push("AI 收录率偏低，关键词 SERP 特征覆盖不足");
      if (contentDirections.length === 0) geoIssues.push("无内容方向，GEO 策略未产出");

      // === DSO 健康度评分（发布数 + 收录率 + 优质内容率） ===
      const dsoTasks = dsoPlan?.tasks || [];
      const matrixPlatforms = dsoMatrix?.platforms || [];
      const publishedCount = matrixPlatforms.filter(
        (p: any) => (p.todayPublished || 0) > 0
      ).length;
      const totalPlatforms = matrixPlatforms.length || 1;
      const indexedRate = Math.round((publishedCount / totalPlatforms) * 100);
      const qualityRate =
        dsoTasks.length > 0
          ? Math.round(
              (dsoTasks.filter((t: any) => t.priority === "high").length /
                dsoTasks.length) *
                100
            )
          : 0;
      const dsoScore = Math.min(
        100,
        Math.round(
          matrixPlatforms.length * 6 + indexedRate * 0.4 + qualityRate * 0.2 + 10
        )
      );
      const dsoIssues: string[] = [];
      if (matrixPlatforms.length === 0) dsoIssues.push("矩阵账号未配置，DSO 分发通道为空");
      if (indexedRate < 30) dsoIssues.push("今日内容收录率偏低，多数平台未发布");
      if (dsoTasks.length === 0) dsoIssues.push("无分发任务，DSO 计划未生成");
      const dormantPlatforms = matrixPlatforms.filter(
        (p: any) => p.status !== "active"
      ).length;
      if (dormantPlatforms > 0)
        dsoIssues.push(`${dormantPlatforms} 个矩阵账号处于非活跃状态`);

      // === GF 健康度评分（线索数 + 转化率 + ROI） ===
      const leadFunnel = gf?.leadFunnel;
      const totalLeads = leadFunnel?.leads || 0;
      const conversionRate = leadFunnel?.conversionRate || 0;
      const avgRoi =
        contentDirections.length > 0
          ? Math.round(
              contentDirections.reduce(
                (s: number, d: any) => s + (d.roiScore || 0),
                0
              ) / contentDirections.length
            )
          : 0;
      const gfScore = Math.min(
        100,
        Math.round(
          totalLeads * 4 + conversionRate * 0.5 + avgRoi * 0.3 + 15
        )
      );
      const gfIssues: string[] = [];
      if (totalLeads === 0) gfIssues.push("CRM 线索为空，GF 输出层未抓取到咨询");
      if (conversionRate === 0) gfIssues.push("转化率为 0，线索到成交链路未跑通");
      if (avgRoi === 0) gfIssues.push("内容 ROI 评分为 0，GEO 策略未生成 ROI");
      if (!gf) gfIssues.push("GrowthFactory Runtime 未注册或查询失败");

      // === 整改清单（基于真实 issues 自动生成） ===
      const actionItems: HealthCheckResult["actionItems"] = [];
      geoIssues.forEach((issue) =>
        actionItems.push({
          priority: geoScore < 40 ? "high" : "medium",
          module: "GEO",
          action: issue,
          expectedImpact: "提升品牌信源覆盖与 AI 收录率",
        })
      );
      dsoIssues.forEach((issue) =>
        actionItems.push({
          priority: dsoScore < 40 ? "high" : "medium",
          module: "DSO",
          action: issue,
          expectedImpact: "提升内容收录率与矩阵活跃度",
        })
      );
      gfIssues.forEach((issue) =>
        actionItems.push({
          priority: gfScore < 40 ? "high" : "medium",
          module: "GF",
          action: issue,
          expectedImpact: "提升线索转化与 ROI",
        })
      );
      // 若知识库文档过少，追加知识层整改项
      const totalDocs = kStats?.totalAssets ?? obsidianDocs.length;
      if (totalDocs < 10) {
        actionItems.push({
          priority: "low",
          module: "Knowledge",
          action: `Obsidian 文档总量仅 ${totalDocs} 篇，建议扩充品牌/产品/案例资料`,
          expectedImpact: "增强 AI 生成内容的品牌约束基底",
        });
      }

      const result: HealthCheckResult = {
        geoHealth: {
          score: geoScore,
          brandSources,
          aiIndexedRate,
          keywordCoverage,
          issues: geoIssues,
        },
        dsoHealth: {
          score: dsoScore,
          publishedCount,
          indexedRate,
          qualityRate,
          issues: dsoIssues,
        },
        gfHealth: {
          score: gfScore,
          totalLeads,
          conversionRate: Math.round(conversionRate * 100) / 100,
          avgRoi,
          issues: gfIssues,
        },
        actionItems,
        checkedAt: new Date().toISOString(),
      };
      setHealthResult(result);

      // 写入体检历史（保留最近 14 次）
      const history = [
        {
          checkedAt: result.checkedAt,
          geo: geoScore,
          dso: dsoScore,
          gf: gfScore,
        },
        ...healthHistory,
      ].slice(0, 14);
      setHealthHistory(history);
      saveLs(LS_KEYS.healthHistory, history);

      setToast({ message: "✅ 体检完成，已生成整改清单", type: "success" });
    } catch (err: any) {
      const msg = err?.message || String(err);
      setError(msg);
      setToast({ message: `❌ 体检失败：${msg}`, type: "error" });
    } finally {
      setChecking(false);
    }
  };

  // ============================================
  // 任务编排：创建任务（写入 localStorage）
  // ============================================
  const handleCreateTask = () => {
    if (!newTask.title.trim()) {
      setToast({ message: "❌ 请填写任务标题", type: "error" });
      return;
    }
    const task: OrchestrationTask = {
      id: `task-${Date.now()}`,
      tier: newTask.tier,
      title: newTask.title.trim(),
      description: newTask.description.trim(),
      status: "scheduled",
      scheduledAt: new Date(newTask.scheduledAt).toISOString(),
      // 按分级预设关联模块动作
      geoActions:
        newTask.tier === "hot_topic"
          ? ["追热点关键词", "抢 SERP 卡位"]
          : newTask.tier === "new_product"
          ? ["新品关键词研究", "产品 SERP 审计"]
          : newTask.tier === "campaign"
          ? ["活动关键词扩展", "引流词研究"]
          : ["品牌词维稳", "长尾词更新"],
      dsoActions:
        newTask.tier === "campaign"
          ? ["多平台矩阵分发", "活动页 SEO"]
          : newTask.tier === "new_product"
          ? ["新品发布矩阵编排"]
          : ["日常分发计划", "矩阵协同"],
      gfActions:
        newTask.tier === "campaign"
          ? ["活动引流爆破", "线索加速转化"]
          : newTask.tier === "new_product"
          ? ["新品线索培育"]
          : ["线索日常跟进", "ROI 监控"],
    };
    const next = [task, ...tasks];
    setTasks(next);
    saveLs(LS_KEYS.tasks, next);
    setNewTask({
      tier: "daily_maintenance",
      title: "",
      description: "",
      scheduledAt: new Date().toISOString().slice(0, 16),
    });
    setCreatingTask(false);
    setToast({ message: "✅ 任务已创建", type: "success" });
  };

  // ============================================
  // 任务编排：执行任务（真实调用 GrowthFactory.run_daily_growth / run_stage）
  // ============================================
  const handleRunTask = async (task: OrchestrationTask) => {
    if (runningTaskId) return;
    setRunningTaskId(task.id);
    try {
      // 标记为运行中
      const startedAt = new Date().toISOString();
      const runningTasks = tasks.map((t) =>
        t.id === task.id ? { ...t, status: "running" as TaskStatus, startedAt } : t
      );
      setTasks(runningTasks);
      saveLs(LS_KEYS.tasks, runningTasks);

      // 真实执行：调用 GrowthFactory 编排（按分级选择 stage）
      let resultMsg = "任务执行完成";
      let success = true;
      if (RuntimeManager.hasRuntime(RuntimeName.GrowthFactory)) {
        // 热点追更 / 新品推广 / 活动引流 优先执行 geo stage（抢卡位/新品/活动词）
        // 日常维稳 执行完整 daily_growth 流程
        const cmd =
          task.tier === "daily_maintenance" ? "run_daily_growth" : "run_stage";
        const params =
          task.tier === "daily_maintenance"
            ? {}
            : { stage: "geo" };
        const r = await RuntimeManager.execute(
          RuntimeName.GrowthFactory,
          cmd,
          params
        );
        success = !!r?.success;
        if (!success) {
          resultMsg = r?.error || "编排执行失败";
        } else {
          const data = (r as any)?.data;
          if (data?.pipeline) {
            resultMsg = `编排完成 ${data.pipeline.completedStages}/${data.pipeline.totalStages} stage`;
          }
        }
      } else {
        resultMsg = "GrowthFactory Runtime 未注册，仅记录任务状态";
        success = false;
      }

      // 更新任务状态
      const completedAt = new Date().toISOString();
      const nextTasks = tasks.map((t) =>
        t.id === task.id
          ? {
              ...t,
              status: (success ? "completed" : "failed") as TaskStatus,
              completedAt,
              result: resultMsg,
            }
          : t
      );
      // 注意：上面 runningTasks 已更新一次，这里基于最新 tasks 再更新
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                status: (success ? "completed" : "failed") as TaskStatus,
                completedAt,
                result: resultMsg,
              }
            : t
        )
      );
      saveLs(LS_KEYS.tasks, nextTasks);
      setToast({
        message: success
          ? `✅ ${task.title} 执行完成`
          : `❌ ${task.title} 执行失败`,
        type: success ? "success" : "error",
      });
    } catch (err: any) {
      const msg = err?.message || String(err);
      const failedAt = new Date().toISOString();
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                status: "failed" as TaskStatus,
                completedAt: failedAt,
                result: msg,
              }
            : t
        )
      );
      saveLs(LS_KEYS.tasks, tasks);
      setToast({ message: `❌ 任务执行异常：${msg}`, type: "error" });
    } finally {
      setRunningTaskId(null);
    }
  };

  // ============================================
  // 任务编排：删除任务
  // ============================================
  const handleDeleteTask = (taskId: string) => {
    const next = tasks.filter((t) => t.id !== taskId);
    setTasks(next);
    saveLs(LS_KEYS.tasks, next);
  };

  // ============================================
  // 舆情监控：扫描真实新闻源，命中负面关键词生成告警
  // ============================================
  const handleScanNews = async () => {
    if (scanning) return;
    setScanning(true);
    setError(null);
    try {
      if (!RuntimeManager.hasRuntime(RuntimeName.News)) {
        setToast({ message: "❌ News Runtime 未注册", type: "error" });
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.News, {
        type: "get_latest",
      });
      const digest = RuntimeManager.unwrapResponse<any>(resp);
      const articles: any[] = digest?.articles || [];
      if (articles.length === 0) {
        setToast({ message: "今日无新闻数据，未扫描到告警", type: "success" });
        return;
      }
      // 扫描标题/摘要命中负面关键词的文章，生成告警
      const newAlerts: SentimentAlert[] = [];
      // v5.2 审计修复 P3-⑥：使用可配置负面词池（用户配置 + 默认池）
      const activePool = loadNegativePool();
      articles.forEach((a: any) => {
        const text = `${a.title || ""} ${a.summary || ""}`.toLowerCase();
        const hitKw = activePool.find((kw) =>
          text.includes(kw.toLowerCase())
        );
        if (hitKw) {
          newAlerts.push({
            id: `alert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            source: a.sourceLabel || a.source || "News",
            type: "negative_mention",
            content: `${a.title || "(无标题)"} — 命中关键词「${hitKw}」`,
            severity: "medium",
            detectedAt: a.publishedAt || new Date().toISOString(),
            handled: false,
          });
        }
      });
      if (newAlerts.length === 0) {
        setToast({
          message: `✅ 已扫描 ${articles.length} 篇新闻，未发现负面信息`,
          type: "success",
        });
        return;
      }
      // 去重合并（基于 content 前缀）
      const existingContents = new Set(alerts.map((a) => a.content.slice(0, 40)));
      const deduped = newAlerts.filter(
        (a) => !existingContents.has(a.content.slice(0, 40))
      );
      const next = [...deduped, ...alerts];
      setAlerts(next);
      saveLs(LS_KEYS.alerts, next);
      setToast({
        message: `⚠️ 扫描到 ${deduped.length} 条负面信息（共扫描 ${articles.length} 篇）`,
        type: "success",
      });
    } catch (err: any) {
      const msg = err?.message || String(err);
      setError(msg);
      setToast({ message: `❌ 扫描失败：${msg}`, type: "error" });
    } finally {
      setScanning(false);
    }
  };

  // ============================================
  // 舆情监控：手动新增告警（用户在其他渠道发现负面信息时录入）
  // ============================================
  const handleAddAlert = () => {
    const id = `alert-${Date.now()}`;
    const next: SentimentAlert[] = [
      {
        id,
        source: "手动录入",
        type: "negative_mention",
        content: "（请编辑告警内容）",
        severity: "low",
        detectedAt: new Date().toISOString(),
        handled: false,
      },
      ...alerts,
    ];
    setAlerts(next);
    saveLs(LS_KEYS.alerts, next);
  };

  // ============================================
  // 舆情监控：处理告警（标记已处理 + 处理备注）
  // ============================================
  const handleProcessAlert = (alertId: string) => {
    const note = handlingNote[alertId]?.trim() || "已处理";
    const next = alerts.map((a) =>
      a.id === alertId ? { ...a, handled: true, handlingNote: note } : a
    );
    setAlerts(next);
    saveLs(LS_KEYS.alerts, next);
    setProcessingId(null);
    setHandlingNote((prev) => {
      const cp = { ...prev };
      delete cp[alertId];
      return cp;
    });
    setToast({ message: "✅ 告警已标记处理", type: "success" });
  };

  // ============================================
  // 舆情监控：删除告警
  // ============================================
  const handleDeleteAlert = (alertId: string) => {
    const next = alerts.filter((a) => a.id !== alertId);
    setAlerts(next);
    saveLs(LS_KEYS.alerts, next);
  };

  // ============================================
  // 派生数据：Obsidian 饼图数据 / 任务按分级分组 / 告警排序 / 趋势图
  // ============================================
  const pieData = useMemo(() => {
    if (!syncStatus) return [];
    return (Object.keys(syncStatus.tagDistribution) as ObsidianTag[])
      .map((tag) => ({
        tag,
        name: TAG_META[tag].label,
        value: syncStatus.tagDistribution[tag],
        color: TAG_META[tag].color,
      }))
      .filter((d) => d.value > 0);
  }, [syncStatus]);

  const tasksByTier = useMemo(() => {
    const map: Record<TaskTier, OrchestrationTask[]> = {
      daily_maintenance: [],
      hot_topic: [],
      new_product: [],
      campaign: [],
    };
    tasks.forEach((t) => map[t.tier].push(t));
    return map;
  }, [tasks]);

  const sortedAlerts = useMemo(() => {
    return [...alerts].sort(
      (a, b) =>
        SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
        +new Date(b.detectedAt) - +new Date(a.detectedAt)
    );
  }, [alerts]);

  // 舆情趋势图：最近 7 天每日告警数
  const alertTrend = useMemo(() => {
    const days: Array<{ date: string; count: number; handled: number }> = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
      const dateStr = d.toISOString().slice(0, 10);
      const dayAlerts = alerts.filter(
        (a) => a.detectedAt.slice(0, 10) === dateStr
      );
      days.push({
        date: dateStr.slice(5), // MM-DD
        count: dayAlerts.length,
        handled: dayAlerts.filter((a) => a.handled).length,
      });
    }
    return days;
  }, [alerts]);

  // 三模块 Runtime 健康度（用于顶部状态条）
  const moduleHealths = useMemo(() => {
    const get = (name: RuntimeName): RuntimeHealth => {
      if (!RuntimeManager.hasRuntime(name)) return "unknown";
      try {
        return RuntimeManager.getRuntime(name).getInfo().health;
      } catch {
        return "unknown";
      }
    };
    return {
      geo: get(RuntimeName.GEO),
      dso: get(RuntimeName.DSO),
      gf: get(RuntimeName.GrowthFactory),
    };
  }, [loading, activeTab]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <div className="rounded-lg border border-purple-500/20 bg-gradient-to-br from-purple-500/[0.04] via-white/[0.01] to-blue-500/[0.02] p-3 backdrop-blur">
      {/* ============ 头部 ============ */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <Brain size={14} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white">
              GEO+DSO 全局管控中台
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              Obsidian 双向互通底座 · 一键体检 · 任务编排 · 舆情监控
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* 三模块健康度状态点 */}
          <div className="flex items-center gap-1.5 mr-1 px-2 py-1 rounded border border-white/[0.06] bg-white/[0.02]">
            {(["geo", "dso", "gf"] as const).map((k) => (
              <span
                key={k}
                className="flex items-center gap-0.5 text-[9px] font-mono"
                style={{ color: MODULE_THEME[k].color }}
                title={`${MODULE_THEME[k].label} Runtime 健康：${moduleHealths[k]}`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: HEALTH_COLOR[moduleHealths[k]] }}
                />
                {MODULE_THEME[k].label}
              </span>
            ))}
          </div>
          <button
            onClick={loadAll}
            disabled={loading}
            className="p-1 text-gray-400 hover:text-gray-200"
            title="刷新数据"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* toast */}
      {toast && (
        <div
          className={`mb-2 flex items-center gap-2 px-3 py-1.5 rounded-md border ${
            toast.type === "success"
              ? "bg-emerald-500/10 border-emerald-500/20"
              : "bg-red-500/10 border-red-500/20"
          }`}
        >
          {toast.type === "success" ? (
            <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
          ) : (
            <XCircle size={11} className="text-red-400 flex-shrink-0" />
          )}
          <span
            className={`text-[10px] ${
              toast.type === "success" ? "text-emerald-300" : "text-red-300"
            }`}
          >
            {toast.message}
          </span>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="mb-2 flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-500/10 border border-red-500/20">
          <AlertCircle size={11} className="text-red-400 flex-shrink-0" />
          <span className="text-[10px] text-red-300">{error}</span>
        </div>
      )}

      {/* 加载中 */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={18} className="animate-spin text-purple-400" />
          <span className="ml-2 text-[11px] text-gray-400 font-mono">
            正在初始化 RuntimeManager...
          </span>
        </div>
      )}

      {/* ============ Tab 导航 ============ */}
      {!loading && (
        <div className="flex items-center gap-1 mb-2 flex-wrap">
          {(
            [
              { id: "obsidian" as const, label: "Obsidian 互通", icon: BookOpen, color: "#A855F7" },
              { id: "health" as const, label: "一键体检", icon: Stethoscope, color: "#3B82F6" },
              { id: "tasks" as const, label: "任务编排", icon: ListTodo, color: "#F59E0B" },
              { id: "sentiment" as const, label: "舆情监控", icon: ShieldAlert, color: "#EF4444" },
            ]
          ).map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-mono transition-all border ${
                  active
                    ? "text-white bg-white/[0.04] border-white/[0.08]"
                    : "text-gray-500 hover:text-gray-300 border-transparent"
                }`}
                style={active ? { color: tab.color } : {}}
              >
                <Icon size={10} />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ============ Tab 内容 ============ */}
      {!loading && (
        <div className="max-h-[460px] overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
            >
              {/* ==================== Obsidian 互通 Tab ==================== */}
              {activeTab === "obsidian" && (
                <div className="space-y-2">
                  {/* 顶部操作栏 */}
                  <div className="flex items-center justify-between">
                    <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                      Obsidian 双向互通底座 · 6 类标签结构化
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handleGfWriteback}
                        disabled={writebacking}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-emerald-600/70 hover:bg-emerald-600 rounded transition-colors disabled:opacity-40"
                        title="从 CRM 抓取真实客户/咨询，写入 Obsidian（反向归档）"
                      >
                        {writebacking ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <ArrowDownUp size={10} />
                        )}
                        GF 反向归档
                      </button>
                      <button
                        onClick={handleSync}
                        disabled={syncing}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-purple-600/80 hover:bg-purple-600 rounded transition-colors disabled:opacity-40"
                        title="一键同步 Vault 并触发 GEO 正向读取"
                      >
                        {syncing ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <RefreshCw size={10} />
                        )}
                        {syncing ? "同步中..." : "一键同步"}
                      </button>
                    </div>
                  </div>

                  {/* 同步统计卡 */}
                  {syncStatus ? (
                    <div className="grid grid-cols-4 gap-1.5">
                      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5">
                        <div className="text-[8px] font-mono text-gray-500">
                          文档总数
                        </div>
                        <div className="text-[14px] font-mono font-bold text-purple-300">
                          {syncStatus.totalDocs}
                        </div>
                      </div>
                      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5">
                        <div className="text-[8px] font-mono text-gray-500">
                          已打标签
                        </div>
                        <div className="text-[14px] font-mono font-bold text-blue-300">
                          {syncStatus.taggedDocs}
                        </div>
                      </div>
                      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5">
                        <div className="text-[8px] font-mono text-gray-500">
                          正向读取
                        </div>
                        <div className="text-[14px] font-mono font-bold text-cyan-300">
                          {syncStatus.geoReads}
                        </div>
                      </div>
                      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5">
                        <div className="text-[8px] font-mono text-gray-500">
                          反向写入
                        </div>
                        <div className="text-[14px] font-mono font-bold text-emerald-300">
                          {syncStatus.gfWritebacks}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-[10px] text-gray-600 text-center">
                      Knowledge Runtime 未注册，无法读取 Obsidian 文档
                    </div>
                  )}

                  {/* 标签分布饼图 + 图例 */}
                  {pieData.length > 0 ? (
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
                      <div className="text-[9px] font-mono text-gray-500 mb-1 uppercase tracking-wider">
                        标签分布（6 类结构化标签）
                      </div>
                      <div className="flex items-stretch gap-2">
                        <div className="h-[160px] flex-1">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={35}
                                outerRadius={65}
                                paddingAngle={2}
                                dataKey="value"
                                nameKey="name"
                              >
                                {pieData.map((entry, i) => (
                                  <Cell
                                    key={`cell-${i}`}
                                    fill={entry.color}
                                    stroke="rgba(10,14,26,0.8)"
                                    strokeWidth={1}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  background: "rgba(10,14,26,0.95)",
                                  border: "1px solid rgba(255,255,255,0.1)",
                                  borderRadius: "6px",
                                  fontSize: "10px",
                                  fontFamily: "monospace",
                                }}
                                labelStyle={{ color: "#E5E7EB" }}
                                itemStyle={{ color: "#9CA3AF" }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="flex-1 flex flex-col justify-center space-y-0.5">
                          {(Object.keys(TAG_META) as ObsidianTag[]).map((tag) => (
                            <div
                              key={tag}
                              className="flex items-center justify-between text-[9px] font-mono"
                            >
                              <span className="flex items-center gap-1">
                                <span
                                  className="w-2 h-2 rounded-sm"
                                  style={{ background: TAG_META[tag].color }}
                                />
                                <span className="text-gray-300">
                                  {TAG_META[tag].label}
                                </span>
                              </span>
                              <span className="text-gray-400">
                                {syncStatus?.tagDistribution[tag] || 0}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-[10px] text-gray-600 text-center">
                      暂无已分类文档（Obsidian Vault 中未匹配到 6 类标签关键词）
                    </div>
                  )}

                  {/* 文档列表 */}
                  <div className="rounded-md border border-white/[0.06] bg-white/[0.02]">
                    <div className="px-2 py-1 border-b border-white/[0.04] text-[9px] font-mono text-gray-500 uppercase tracking-wider flex items-center justify-between">
                      <span>Obsidian 文档（含读取/写入状态）</span>
                      <span>{obsidianDocs.length} 篇</span>
                    </div>
                    <div className="max-h-[180px] overflow-y-auto">
                      {obsidianDocs.length === 0 ? (
                        <div className="p-3 text-[10px] text-gray-600 text-center">
                          Obsidian Vault 为空，或 Knowledge Runtime 未就绪
                        </div>
                      ) : (
                        obsidianDocs.slice(0, 50).map((doc, i) => (
                          <div
                            key={`${doc.path}-${i}`}
                            className="px-2 py-1.5 border-b border-white/[0.03] hover:bg-white/[0.02] flex items-start gap-2"
                          >
                            <FileText
                              size={10}
                              className="text-gray-500 flex-shrink-0 mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-gray-200 truncate">
                                  {doc.title}
                                </span>
                                {doc.writtenByGf && (
                                  <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400 flex-shrink-0">
                                    GF 写入
                                  </span>
                                )}
                                {doc.geoReadCount > 0 && (
                                  <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-cyan-500/15 text-cyan-400 flex-shrink-0">
                                    GEO 读 {doc.geoReadCount}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                {doc.tags.length === 0 ? (
                                  <span className="text-[8px] font-mono text-gray-600">
                                    未分类
                                  </span>
                                ) : (
                                  doc.tags.map((t) => (
                                    <span
                                      key={t}
                                      className="text-[8px] font-mono px-1 py-0.5 rounded"
                                      style={{
                                        background: `${TAG_META[t].color}20`,
                                        color: TAG_META[t].color,
                                      }}
                                    >
                                      {TAG_META[t].label}
                                    </span>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* 上次同步时间 */}
                  {syncStatus?.lastSyncAt && (
                    <div className="text-[8px] font-mono text-gray-600 text-right">
                      上次同步：{new Date(syncStatus.lastSyncAt).toLocaleString("zh-CN")}
                    </div>
                  )}
                </div>
              )}

              {/* ==================== 一键体检 Tab ==================== */}
              {activeTab === "health" && (
                <div className="space-y-2">
                  {/* 顶部操作栏 */}
                  <div className="flex items-center justify-between">
                    <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                      三模块健康度体检 · 整改清单
                    </div>
                    <button
                      onClick={handleHealthCheck}
                      disabled={checking}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-purple-600/80 hover:bg-purple-600 rounded transition-colors disabled:opacity-40"
                      title="一键检测 GEO/DSO/GF 健康度"
                    >
                      {checking ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : (
                        <Stethoscope size={10} />
                      )}
                      {checking ? "体检中..." : "一键体检"}
                    </button>
                  </div>

                  {/* 三模块评分卡 */}
                  {healthResult ? (
                    <div className="grid grid-cols-3 gap-1.5">
                      {(
                        [
                          {
                            key: "geo" as const,
                            score: healthResult.geoHealth.score,
                            meta: MODULE_THEME.geo,
                            items: [
                              { label: "品牌信源", value: healthResult.geoHealth.brandSources },
                              { label: "AI 收录率", value: `${healthResult.geoHealth.aiIndexedRate}%` },
                              { label: "关键词覆盖", value: `${healthResult.geoHealth.keywordCoverage}%` },
                            ],
                          },
                          {
                            key: "dso" as const,
                            score: healthResult.dsoHealth.score,
                            meta: MODULE_THEME.dso,
                            items: [
                              { label: "已发布", value: healthResult.dsoHealth.publishedCount },
                              { label: "收录率", value: `${healthResult.dsoHealth.indexedRate}%` },
                              { label: "优质率", value: `${healthResult.dsoHealth.qualityRate}%` },
                            ],
                          },
                          {
                            key: "gf" as const,
                            score: healthResult.gfHealth.score,
                            meta: MODULE_THEME.gf,
                            items: [
                              { label: "线索数", value: healthResult.gfHealth.totalLeads },
                              { label: "转化率", value: `${healthResult.gfHealth.conversionRate}%` },
                              { label: "平均 ROI", value: healthResult.gfHealth.avgRoi },
                            ],
                          },
                        ]
                      ).map((card) => {
                        const Icon = card.meta.icon;
                        return (
                          <div
                            key={card.key}
                            className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2 overflow-hidden"
                          >
                            <div
                              className="h-0.5 w-full mb-1.5"
                              style={{ background: card.meta.color }}
                            />
                            <div className="flex items-center gap-1 mb-1">
                              <Icon size={10} style={{ color: card.meta.color }} />
                              <span className="text-[9px] font-mono text-gray-300">
                                {card.meta.label}
                              </span>
                            </div>
                            <div
                              className="text-[20px] font-mono font-bold leading-none mb-1"
                              style={{ color: scoreColor(card.score) }}
                            >
                              {card.score}
                              <span className="text-[10px] text-gray-500 ml-0.5">
                                /100
                              </span>
                            </div>
                            <div className="space-y-0.5">
                              {card.items.map((it) => (
                                <div
                                  key={it.label}
                                  className="flex justify-between text-[8px] font-mono"
                                >
                                  <span className="text-gray-500">{it.label}</span>
                                  <span className="text-gray-300">{it.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-4 text-[10px] text-gray-600 text-center">
                      尚未体检，点击"一键体检"检测 GEO/DSO/GF 健康度
                    </div>
                  )}

                  {/* 整改清单 */}
                  {healthResult && (
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02]">
                      <div className="px-2 py-1 border-b border-white/[0.04] text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                        整改清单（{healthResult.actionItems.length} 项）
                      </div>
                      <div className="max-h-[150px] overflow-y-auto">
                        {healthResult.actionItems.length === 0 ? (
                          <div className="p-3 text-[10px] text-emerald-400 text-center">
                            ✅ 全部模块健康，无整改项
                          </div>
                        ) : (
                          healthResult.actionItems.map((item, i) => (
                            <div
                              key={i}
                              className="px-2 py-1.5 border-b border-white/[0.03] flex items-start gap-2"
                            >
                              <span
                                className="text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0 mt-0.5"
                                style={{
                                  background:
                                    item.priority === "high"
                                      ? "rgba(239,68,68,0.15)"
                                      : item.priority === "medium"
                                      ? "rgba(245,158,11,0.15)"
                                      : "rgba(107,114,128,0.15)",
                                  color:
                                    item.priority === "high"
                                      ? "#EF4444"
                                      : item.priority === "medium"
                                      ? "#F59E0B"
                                      : "#9CA3AF",
                                }}
                              >
                                {item.priority === "high"
                                  ? "高"
                                  : item.priority === "medium"
                                  ? "中"
                                  : "低"}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <span className="text-[9px] font-mono text-purple-300">
                                    [{item.module}]
                                  </span>
                                  <span className="text-[10px] text-gray-200">
                                    {item.action}
                                  </span>
                                </div>
                                <div className="text-[8px] font-mono text-gray-500 mt-0.5">
                                  预期：{item.expectedImpact}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* 体检历史趋势 */}
                  {healthHistory.length > 0 && (
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
                      <div className="text-[9px] font-mono text-gray-500 mb-1 uppercase tracking-wider">
                        体检历史（最近 {healthHistory.length} 次）
                      </div>
                      <div className="h-[100px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={healthHistory
                              .slice(0, 14)
                              .reverse()
                              .map((h) => ({
                                date: new Date(h.checkedAt)
                                  .toLocaleString("zh-CN", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }),
                                GEO: h.geo,
                                DSO: h.dso,
                                GF: h.gf,
                              }))}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="rgba(255,255,255,0.04)"
                            />
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 8, fill: "#6B7280", fontFamily: "monospace" }}
                              tickLine={false}
                              axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                            />
                            <YAxis
                              domain={[0, 100]}
                              tick={{ fontSize: 8, fill: "#6B7280", fontFamily: "monospace" }}
                              tickLine={false}
                              axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                            />
                            <Tooltip
                              contentStyle={{
                                background: "rgba(10,14,26,0.95)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                borderRadius: "6px",
                                fontSize: "10px",
                                fontFamily: "monospace",
                              }}
                              labelStyle={{ color: "#E5E7EB" }}
                            />
                            <Bar dataKey="GEO" fill="#3B82F6" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="DSO" fill="#F59E0B" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="GF" fill="#10B981" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex items-center justify-center gap-3 mt-1 text-[8px] font-mono">
                        <span className="flex items-center gap-0.5">
                          <span className="w-2 h-2 rounded-sm bg-blue-500" /> GEO
                        </span>
                        <span className="flex items-center gap-0.5">
                          <span className="w-2 h-2 rounded-sm bg-amber-500" /> DSO
                        </span>
                        <span className="flex items-center gap-0.5">
                          <span className="w-2 h-2 rounded-sm bg-emerald-500" /> GF
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ==================== 任务编排 Tab ==================== */}
              {activeTab === "tasks" && (
                <div className="space-y-2">
                  {/* 四类任务分级卡 */}
                  <div className="grid grid-cols-2 gap-1.5">
                    {(Object.keys(TASK_TIER_META) as TaskTier[]).map((tier) => {
                      const meta = TASK_TIER_META[tier];
                      const Icon = meta.icon;
                      const tierTasks = tasksByTier[tier];
                      return (
                        <div
                          key={tier}
                          className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5 cursor-pointer hover:bg-white/[0.04] transition-colors"
                          onClick={() =>
                            setNewTask((p) => ({ ...p, tier }))
                          }
                          style={{
                            borderColor:
                              newTask.tier === tier
                                ? `${meta.color}40`
                                : undefined,
                          }}
                        >
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Icon size={10} style={{ color: meta.color }} />
                            <span
                              className="text-[10px] font-mono"
                              style={{ color: meta.color }}
                            >
                              {meta.label}
                            </span>
                            <span className="text-[8px] font-mono text-gray-500 ml-auto">
                              {tierTasks.length}
                            </span>
                          </div>
                          <div className="text-[8px] font-mono text-gray-500">
                            {meta.desc}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* 创建任务表单 */}
                  <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
                    <button
                      onClick={() => setCreatingTask((v) => !v)}
                      className="flex items-center gap-1 text-[10px] font-mono text-purple-300 hover:text-purple-200"
                    >
                      <Plus size={10} />
                      {creatingTask ? "收起创建表单" : "创建新任务"}
                      <ChevronDown
                        size={9}
                        className={creatingTask ? "rotate-180 transition-transform" : "transition-transform"}
                      />
                    </button>
                    {creatingTask && (
                      <div className="mt-2 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[9px] font-mono text-gray-500 w-12">
                            分级
                          </span>
                          <select
                            value={newTask.tier}
                            onChange={(e) =>
                              setNewTask((p) => ({
                                ...p,
                                tier: e.target.value as TaskTier,
                              }))
                            }
                            className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded px-1.5 py-1 text-[10px] text-gray-200 font-mono"
                          >
                            {(Object.keys(TASK_TIER_META) as TaskTier[]).map((t) => (
                              <option key={t} value={t} className="bg-gray-900">
                                {TASK_TIER_META[t].label} - {TASK_TIER_META[t].desc}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[9px] font-mono text-gray-500 w-12">
                            标题
                          </span>
                          <input
                            value={newTask.title}
                            onChange={(e) =>
                              setNewTask((p) => ({ ...p, title: e.target.value }))
                            }
                            placeholder="如：今日品牌词维稳任务"
                            className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded px-1.5 py-1 text-[10px] text-gray-200 font-mono placeholder-gray-600"
                          />
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[9px] font-mono text-gray-500 w-12">
                            描述
                          </span>
                          <input
                            value={newTask.description}
                            onChange={(e) =>
                              setNewTask((p) => ({
                                ...p,
                                description: e.target.value,
                              }))
                            }
                            placeholder="任务描述（可选）"
                            className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded px-1.5 py-1 text-[10px] text-gray-200 font-mono placeholder-gray-600"
                          />
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[9px] font-mono text-gray-500 w-12">
                            调度
                          </span>
                          <input
                            type="datetime-local"
                            value={newTask.scheduledAt}
                            onChange={(e) =>
                              setNewTask((p) => ({
                                ...p,
                                scheduledAt: e.target.value,
                              }))
                            }
                            className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded px-1.5 py-1 text-[10px] text-gray-200 font-mono"
                          />
                        </div>
                        <button
                          onClick={handleCreateTask}
                          className="w-full flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-purple-600/80 hover:bg-purple-600 rounded transition-colors"
                        >
                          <Plus size={10} />
                          创建任务
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 任务列表 */}
                  <div className="rounded-md border border-white/[0.06] bg-white/[0.02]">
                    <div className="px-2 py-1 border-b border-white/[0.04] text-[9px] font-mono text-gray-500 uppercase tracking-wider flex items-center justify-between">
                      <span>任务列表（按分级分组）</span>
                      <span>{tasks.length} 个</span>
                    </div>
                    <div className="max-h-[220px] overflow-y-auto">
                      {tasks.length === 0 ? (
                        <div className="p-3 text-[10px] text-gray-600 text-center">
                          暂无任务，点击"创建新任务"开始编排
                        </div>
                      ) : (
                        (Object.keys(TASK_TIER_META) as TaskTier[]).map((tier) => {
                          const tierTasks = tasksByTier[tier];
                          if (tierTasks.length === 0) return null;
                          const meta = TASK_TIER_META[tier];
                          const TierIcon = meta.icon;
                          return (
                            <div key={tier}>
                              <div
                                className="px-2 py-0.5 text-[8px] font-mono uppercase tracking-wider flex items-center gap-1"
                                style={{ color: meta.color }}
                              >
                                <TierIcon size={9} />
                                {meta.label}
                              </div>
                              {tierTasks.map((task) => (
                                <div
                                  key={task.id}
                                  className="px-2 py-1.5 border-b border-white/[0.03] hover:bg-white/[0.02]"
                                >
                                  <div className="flex items-start justify-between gap-1.5">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] text-gray-200 truncate">
                                          {task.title}
                                        </span>
                                        <span
                                          className="text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0"
                                          style={{
                                            background:
                                              task.status === "completed"
                                                ? "rgba(16,185,129,0.15)"
                                                : task.status === "running"
                                                ? "rgba(59,130,246,0.15)"
                                                : task.status === "failed"
                                                ? "rgba(239,68,68,0.15)"
                                                : "rgba(107,114,128,0.15)",
                                            color:
                                              task.status === "completed"
                                                ? "#10B981"
                                                : task.status === "running"
                                                ? "#3B82F6"
                                                : task.status === "failed"
                                                ? "#EF4444"
                                                : "#9CA3AF",
                                          }}
                                        >
                                          {task.status === "scheduled"
                                            ? "已调度"
                                            : task.status === "running"
                                            ? "运行中"
                                            : task.status === "completed"
                                            ? "已完成"
                                            : "失败"}
                                        </span>
                                      </div>
                                      {task.description && (
                                        <div className="text-[8px] text-gray-500 mt-0.5">
                                          {task.description}
                                        </div>
                                      )}
                                      <div className="text-[8px] font-mono text-gray-600 mt-0.5 flex items-center gap-2">
                                        <span className="flex items-center gap-0.5">
                                          <Clock size={8} />
                                          {new Date(task.scheduledAt).toLocaleString(
                                            "zh-CN",
                                            {
                                              month: "2-digit",
                                              day: "2-digit",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            }
                                          )}
                                        </span>
                                        {task.completedAt && (
                                          <span>
                                            → 完成{" "}
                                            {new Date(task.completedAt).toLocaleString(
                                              "zh-CN",
                                              {
                                                month: "2-digit",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              }
                                            )}
                                          </span>
                                        )}
                                      </div>
                                      {task.result && (
                                        <div className="text-[8px] font-mono text-gray-500 mt-0.5">
                                          {task.result}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex flex-col gap-1 flex-shrink-0">
                                      {(task.status === "scheduled" ||
                                        task.status === "failed") && (
                                        <button
                                          onClick={() => handleRunTask(task)}
                                          disabled={runningTaskId === task.id}
                                          className="flex items-center gap-0.5 px-1.5 py-0.5 text-[8px] font-mono text-white bg-purple-600/70 hover:bg-purple-600 rounded transition-colors disabled:opacity-40"
                                          title="执行任务（调用 GrowthFactory 编排）"
                                        >
                                          {runningTaskId === task.id ? (
                                            <Loader2 size={8} className="animate-spin" />
                                          ) : (
                                            <Play size={8} />
                                          )}
                                          执行
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleDeleteTask(task.id)}
                                        className="flex items-center gap-0.5 px-1.5 py-0.5 text-[8px] font-mono text-gray-400 hover:text-red-300 bg-white/[0.04] hover:bg-red-500/10 rounded transition-colors"
                                      >
                                        <XCircle size={8} />
                                        删除
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* 运行状态看板 */}
                  <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
                    <div className="text-[9px] font-mono text-gray-500 mb-1 uppercase tracking-wider">
                      运行状态看板
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      {(["scheduled", "running", "completed", "failed"] as TaskStatus[]).map(
                        (st) => {
                          const count = tasks.filter((t) => t.status === st).length;
                          const color =
                            st === "completed"
                              ? "#10B981"
                              : st === "running"
                              ? "#3B82F6"
                              : st === "failed"
                              ? "#EF4444"
                              : "#6B7280";
                          const label =
                            st === "scheduled"
                              ? "已调度"
                              : st === "running"
                              ? "运行中"
                              : st === "completed"
                              ? "已完成"
                              : "失败";
                          return (
                            <div
                              key={st}
                              className="text-center rounded border border-white/[0.06] bg-white/[0.02] py-1"
                            >
                              <div
                                className="text-[14px] font-mono font-bold"
                                style={{ color }}
                              >
                                {count}
                              </div>
                              <div className="text-[8px] font-mono text-gray-500">
                                {label}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ==================== 舆情监控 Tab ==================== */}
              {activeTab === "sentiment" && (
                <div className="space-y-2">
                  {/* 顶部操作栏 */}
                  <div className="flex items-center justify-between">
                    <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                      全网平台 + AI 模型负面信息监测
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handleAddAlert}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-gray-300 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded transition-colors"
                        title="手动录入告警"
                      >
                        <Plus size={10} />
                        手动录入
                      </button>
                      <button
                        onClick={handleScanNews}
                        disabled={scanning}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-red-600/70 hover:bg-red-600 rounded transition-colors disabled:opacity-40"
                        title="扫描今日新闻源，命中负面关键词生成告警"
                      >
                        {scanning ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <ShieldAlert size={10} />
                        )}
                        {scanning ? "扫描中..." : "扫描新闻源"}
                      </button>
                    </div>
                  </div>

                  {/* v5.2 审计修复 P3-⑥：负面预警关键词池配置（品牌 × 负面词，localStorage） */}
                  <div className="mt-2 rounded-md border border-red-500/10 bg-red-500/[0.02] p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[8px] font-mono text-red-300/80 uppercase tracking-wider">
                        负面预警关键词池（扫描时命中即告警）
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-1.5">
                      {loadNegativePool().slice(0, 12).map((kw, i) => (
                        <span key={i} className="text-[8px] font-mono px-1 py-0.5 rounded bg-red-500/10 text-red-300/80 border border-red-500/20">
                          {kw}
                        </span>
                      ))}
                    </div>
                    <input
                      placeholder="添加品牌×负面词，如：XEAOS 骗局 / 小舍AI 跑路"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const v = (e.target as HTMLInputElement).value.trim();
                          if (v) {
                            const existing = loadNegativePool();
                            if (!existing.includes(v)) {
                              saveNegativePoolCustom([...((JSON.parse(localStorage.getItem(NEGATIVE_POOL_KEY) || "[]") || []) as string[]), v]);
                            }
                            (e.target as HTMLInputElement).value = "";
                          }
                        }
                      }}
                      className="w-full px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[9px] text-gray-300 outline-none focus:border-red-500/40"
                    />
                  </div>

                  {/* 告警统计 */}
                  <div className="grid grid-cols-4 gap-1.5">
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5">
                      <div className="text-[8px] font-mono text-gray-500">告警总数</div>
                      <div className="text-[14px] font-mono font-bold text-red-300">
                        {alerts.length}
                      </div>
                    </div>
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5">
                      <div className="text-[8px] font-mono text-gray-500">紧急/高</div>
                      <div className="text-[14px] font-mono font-bold text-orange-300">
                        {alerts.filter(
                          (a) => a.severity === "critical" || a.severity === "high"
                        ).length}
                      </div>
                    </div>
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5">
                      <div className="text-[8px] font-mono text-gray-500">未处理</div>
                      <div className="text-[14px] font-mono font-bold text-amber-300">
                        {alerts.filter((a) => !a.handled).length}
                      </div>
                    </div>
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5">
                      <div className="text-[8px] font-mono text-gray-500">已处理</div>
                      <div className="text-[14px] font-mono font-bold text-emerald-300">
                        {alerts.filter((a) => a.handled).length}
                      </div>
                    </div>
                  </div>

                  {/* 趋势图 */}
                  {alerts.length > 0 && (
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2">
                      <div className="text-[9px] font-mono text-gray-500 mb-1 uppercase tracking-wider">
                        近 7 天告警趋势
                      </div>
                      <div className="h-[90px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={alertTrend}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="rgba(255,255,255,0.04)"
                            />
                            <XAxis
                              dataKey="date"
                              tick={{
                                fontSize: 8,
                                fill: "#6B7280",
                                fontFamily: "monospace",
                              }}
                              tickLine={false}
                              axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                            />
                            <YAxis
                              tick={{
                                fontSize: 8,
                                fill: "#6B7280",
                                fontFamily: "monospace",
                              }}
                              tickLine={false}
                              axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                              allowDecimals={false}
                            />
                            <Tooltip
                              contentStyle={{
                                background: "rgba(10,14,26,0.95)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                borderRadius: "6px",
                                fontSize: "10px",
                                fontFamily: "monospace",
                              }}
                              labelStyle={{ color: "#E5E7EB" }}
                            />
                            <Bar dataKey="count" name="总数" fill="#EF4444" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="handled" name="已处理" fill="#10B981" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* 告警列表（按严重程度排序） */}
                  <div className="rounded-md border border-white/[0.06] bg-white/[0.02]">
                    <div className="px-2 py-1 border-b border-white/[0.04] text-[9px] font-mono text-gray-500 uppercase tracking-wider flex items-center justify-between">
                      <span>告警列表（按严重程度排序）</span>
                      <span>{alerts.length} 条</span>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                      {sortedAlerts.length === 0 ? (
                        <div className="p-3 text-[10px] text-gray-600 text-center">
                          暂无告警，点击"扫描新闻源"自动监测负面信息
                        </div>
                      ) : (
                        sortedAlerts.map((alert) => (
                          <div
                            key={alert.id}
                            className="px-2 py-1.5 border-b border-white/[0.03] hover:bg-white/[0.02]"
                          >
                            <div className="flex items-start gap-1.5">
                              <span
                                className="text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0 mt-0.5"
                                style={{
                                  background: `${SEVERITY_COLOR[alert.severity]}20`,
                                  color: SEVERITY_COLOR[alert.severity],
                                }}
                              >
                                {SEVERITY_LABEL[alert.severity]}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span
                                    className="text-[8px] font-mono px-1 py-0.5 rounded"
                                    style={{
                                      background: `${ALERT_TYPE_META[alert.type].color}20`,
                                      color: ALERT_TYPE_META[alert.type].color,
                                    }}
                                  >
                                    {ALERT_TYPE_META[alert.type].label}
                                  </span>
                                  <span className="text-[8px] font-mono text-gray-500">
                                    来源：{alert.source}
                                  </span>
                                  <span className="text-[8px] font-mono text-gray-600">
                                    {new Date(alert.detectedAt).toLocaleString("zh-CN", {
                                      month: "2-digit",
                                      day: "2-digit",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </span>
                                  {alert.handled && (
                                    <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400">
                                      已处理
                                    </span>
                                  )}
                                </div>
                                <div className="text-[10px] text-gray-200 mt-0.5">
                                  {alert.content}
                                </div>
                                {alert.handlingNote && (
                                  <div className="text-[8px] font-mono text-emerald-400 mt-0.5">
                                    处理记录：{alert.handlingNote}
                                  </div>
                                )}
                                {/* 处理表单 */}
                                {!alert.handled && processingId === alert.id && (
                                  <div className="mt-1 flex items-center gap-1">
                                    <input
                                      value={handlingNote[alert.id] || ""}
                                      onChange={(e) =>
                                        setHandlingNote((p) => ({
                                          ...p,
                                          [alert.id]: e.target.value,
                                        }))
                                      }
                                      placeholder="处理备注（如：已联系平台下架）"
                                      className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded px-1.5 py-0.5 text-[9px] text-gray-200 font-mono placeholder-gray-600"
                                    />
                                    <button
                                      onClick={() => handleProcessAlert(alert.id)}
                                      className="px-1.5 py-0.5 text-[9px] font-mono text-white bg-emerald-600/70 hover:bg-emerald-600 rounded"
                                    >
                                      确认
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-1 flex-shrink-0">
                                {!alert.handled &&
                                  (processingId === alert.id ? (
                                    <button
                                      onClick={() => setProcessingId(null)}
                                      className="px-1.5 py-0.5 text-[8px] font-mono text-gray-400 bg-white/[0.04] rounded"
                                    >
                                      取消
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => setProcessingId(alert.id)}
                                      className="flex items-center gap-0.5 px-1.5 py-0.5 text-[8px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded"
                                    >
                                      <CheckCircle2 size={8} />
                                      处理
                                    </button>
                                  ))}
                                <button
                                  onClick={() => handleDeleteAlert(alert.id)}
                                  className="flex items-center gap-0.5 px-1.5 py-0.5 text-[8px] font-mono text-gray-400 hover:text-red-300 bg-white/[0.04] hover:bg-red-500/10 rounded"
                                >
                                  <XCircle size={8} />
                                  删除
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {/* ============ 底部说明 ============ */}
      <div className="mt-2 pt-2 border-t border-white/[0.04] flex items-center justify-between text-[8px] font-mono text-gray-600">
        <span className="flex items-center gap-1">
          <Wand2 size={8} className="text-purple-600" />
          数据来源：RuntimeManager（Knowledge / GEO / DSO / GrowthFactory / CRM / News）
        </span>
        <span className="flex items-center gap-1">
          <Sparkles size={8} className="text-purple-500" />
          GEO+DSO 商业化改造 · 第 1 层 + 第 5 层
        </span>
      </div>
    </div>
  );
}
